import { ReactNode } from 'react';
import { useMaintenanceMode } from '@/hooks/useKillSwitch';
import { Wrench, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';

interface MaintenanceModeProps {
  children: ReactNode;
}

/**
 * Global maintenance mode wrapper
 * When maintenance_mode is enabled, shows maintenance page instead of app
 * 
 * FAIL-OPEN STRATEGY: If we can't fetch flags, let the app through
 * This prevents the app from being "bricked" if Supabase is down
 */
export function MaintenanceMode({ children }: MaintenanceModeProps) {
  const { isMaintenanceMode, isLoading, error } = useMaintenanceMode();

  // FAIL-OPEN: If there's an error fetching flags, let the app through
  // Better to show the app than to show nothing or maintenance page
  if (error) {
    logger.warn('[MaintenanceMode] Failed to fetch flags, failing open', { error: String(error) });
    return <>{children}</>;
  }

  // Don't block during initial load (fail-open during loading too)
  if (isLoading) {
    return <>{children}</>;
  }

  // If in maintenance mode, show maintenance page
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return <>{children}</>;
}

function MaintenancePage() {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md text-center px-6">
        <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <Wrench className="h-10 w-10 text-primary" />
        </div>
        
        <h1 className="text-3xl font-bold mb-4">
          Maintenance en cours
        </h1>
        
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Nous effectuons actuellement une maintenance programmée pour améliorer votre expérience.
          Le service sera rétabli dans quelques instants.
        </p>
        
        <div className="space-y-4">
          <Button onClick={handleRefresh} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Actualiser la page
          </Button>
          
          <p className="text-xs text-muted-foreground">
            Merci de votre patience.
          </p>
        </div>
      </div>
    </div>
  );
}
